import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";

export interface JiraConfig {
  enabled: boolean;
  write_enabled: boolean;
  base_url: string;
  project_keys: string[];
  cache_ttl_minutes: number;
}

export interface JiraLinkedParent {
  scope_type: "project" | "initiative";
  id: string;
  name: string;
}

export interface JiraIssue {
  issue_key: string;
  summary: string | null;
  status: string | null;
  status_category: "new" | "indeterminate" | "done" | null;
  assignee: string | null;
  priority: string | null;
  issue_type: string | null;
  url: string | null;
  updated_at: string | null;
  fetched_at: string | null;
  parent_key?: string | null;
  pct_complete?: number | null;
  /** Synced from the team's "Project Health" custom field. Normalized
   * green/yellow/red or null when absent / unrecognized. */
  health?: string | null;
  start_date?: string | null;
  due_date?: string | null;
  /** Team's "Dev Start" / "Dev End" custom-field values. Drive the
   *  Project Detail Jira table's schedule columns. ISO YYYY-MM-DD or
   *  null when absent on the Jira side. */
  dev_start_date?: string | null;
  dev_end_date?: string | null;
  children?: JiraIssue[];
  last_sync_status?: "ok" | "error" | null;
  last_sync_error?: string | null;
  /** Every planner item this issue is linked to (projects + initiatives).
   *  Used by the "Also linked to" chip on each card. Frontend filters
   *  the current parent out when rendering. */
  linked_to?: JiraLinkedParent[];
  /** Rollup attribution — set when the issue is showing on an
   *  initiative's Jira tab via a supporting project link (not a
   *  direct initiative link). Drives the "via ETE-XX" chip on the
   *  card. Null on direct links and on project-side panels. */
  via_project_id?: string | null;
  via_project_name?: string | null;
}

export interface JiraIssueList {
  /** Exactly one of project_id / initiative_id is set, matching the
   *  endpoint that produced the response. */
  project_id?: string | null;
  initiative_id?: string | null;
  total: number;
  done: number;
  issues: JiraIssue[];
}

export type JiraParentType = "project";

export interface JiraSupports {
  project_id: string;
  project_name: string;
  team: string | null;
  department: string | null;
  sponsor: string | null;
  health: string | null;
  pct_complete: number;
  status: string | null;
  start_date: string | null;
  end_date: string | null;
}

export interface JiraComment {
  author: string | null;
  created: string | null;
  body_html: string;
}

export interface JiraIssueDetail {
  issue: JiraIssue;
  supports: JiraSupports | null;
  description_html: string;
  reporter: string | null;
  due_date: string | null;
  created_at: string | null;
  labels: string[];
  comments: JiraComment[];
  comment_count: number;
  detail_fetched_at: string | null;
}

export interface JiraTransition {
  id: string;
  name: string;
  to_status: string | null;
}

export const jiraKeys = {
  config: ["jira", "config"] as const,
  projectIssues: (projectId: string) => ["jira", "project", projectId] as const,
  parentIssues: (parentType: JiraParentType, parentId: string) =>
    ["jira", parentType, parentId] as const,
  transitions: (issueKey: string) => ["jira", "transitions", issueKey] as const,
  issueDetail: (projectId: string, issueKey: string) =>
    ["jira", "detail", projectId, issueKey] as const,
  boards: ["jira", "boards"] as const,
};

// URL prefix per parent type.
function _parentPrefix(parentType: JiraParentType, parentId: string): string {
  return `/jira/${parentType}s/${parentId}`;
}

/**
 * Drawer detail fetch. Two API paths:
 *  - projectId set     → /jira/projects/{projectId}/issues/{key}/detail
 *                        (carries the planner-side 'Supports' banner)
 *  - projectId null    → /jira/issues/{key}/detail
 *                        (project-agnostic — used by the Synnergie page)
 */
export function useJiraIssueDetail(projectId: string | null, issueKey: string | null) {
  return useQuery<JiraIssueDetail>({
    queryKey: jiraKeys.issueDetail(projectId ?? "", issueKey ?? ""),
    queryFn: () => {
      const url = projectId
        ? `/jira/projects/${projectId}/issues/${issueKey}/detail`
        : `/jira/issues/${issueKey}/detail`;
      return api.get(url).then((r) => r.data);
    },
    enabled: Boolean(issueKey),
    // Drawer is short-lived; refetch on remount but cache 30s so
    // open-close-open doesn't double-hit the API.
    staleTime: 30_000,
  });
}

// ---- Jira board catalog ---------------------------------------------------
export type JiraBoardState = "tracked" | "available" | "hidden";

export interface JiraBoard {
  key: string;
  name: string;
  project_type: string | null;
  state: JiraBoardState;
  synced_at: string | null;
}

export interface JiraBoardList {
  boards: JiraBoard[];
  synced: boolean;
}

/** The board catalog + curation state. Auto-syncs from Jira server-side
 *  on first access, so the picker is populated without a manual step. */
export function useJiraBoards() {
  return useQuery<JiraBoardList>({
    queryKey: jiraKeys.boards,
    queryFn: () => api.get("/jira/boards").then((r) => r.data),
  });
}

/** Refresh the catalog from Jira. Preserves curation; new boards arrive
 *  as 'available'. */
export function useSyncJiraBoards() {
  const qc = useQueryClient();
  return useMutation<JiraBoardList, Error, void>({
    mutationFn: () => api.post("/jira/boards/sync").then((r) => r.data),
    onSuccess: (data) => {
      qc.setQueryData(jiraKeys.boards, data);
      qc.invalidateQueries({ queryKey: jiraKeys.config });
    },
  });
}

/** Set one board's state (track / available / hidden). */
export function useSetJiraBoardState() {
  const qc = useQueryClient();
  return useMutation<JiraBoard, Error, { key: string; state: JiraBoardState }>({
    mutationFn: ({ key, state }) =>
      api.patch(`/jira/boards/${encodeURIComponent(key)}`, { state }).then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: jiraKeys.boards });
      qc.invalidateQueries({ queryKey: jiraKeys.config });
    },
  });
}

/** Set many boards to one state at once (e.g. hide all service desks). */
export function useBulkSetJiraBoardState() {
  const qc = useQueryClient();
  return useMutation<JiraBoardList, Error, { keys: string[]; state: JiraBoardState }>({
    mutationFn: (payload) => api.post("/jira/boards/bulk", payload).then((r) => r.data),
    onSuccess: (data) => {
      qc.setQueryData(jiraKeys.boards, data);
      qc.invalidateQueries({ queryKey: jiraKeys.config });
    },
  });
}

/** Integration status. Cached forever — values come from env vars at
 * server startup, so they don't change without a redeploy. */
export function useJiraConfig() {
  return useQuery<JiraConfig>({
    queryKey: jiraKeys.config,
    queryFn: () => api.get("/jira/config").then((r) => r.data),
    staleTime: Infinity,
  });
}

// Scope-aware hooks. The legacy useProjectJiraIssues / useLink / useUnlink
// / useRefresh wrappers below keep ProjectDetail compiling without changes.

export function useJiraIssues(
  parentType: JiraParentType,
  parentId: string | null,
) {
  return useQuery<JiraIssueList>({
    queryKey: jiraKeys.parentIssues(parentType, parentId ?? ""),
    queryFn: () =>
      api.get(`${_parentPrefix(parentType, parentId ?? "")}/issues`).then((r) => r.data),
    enabled: Boolean(parentId),
  });
}

export function useLinkJiraIssueScoped(
  parentType: JiraParentType,
  parentId: string,
) {
  const qc = useQueryClient();
  return useMutation<
    { link: unknown; issue: JiraIssue },
    Error,
    { issue_key_or_url: string; linked_by?: string }
  >({
    mutationFn: (payload) =>
      api
        .post(`${_parentPrefix(parentType, parentId)}/links`, payload)
        .then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: jiraKeys.parentIssues(parentType, parentId) });
      qc.invalidateQueries({ queryKey: ["portfolio"] });
    },
  });
}

export function useUnlinkJiraIssueScoped(
  parentType: JiraParentType,
  parentId: string,
) {
  const qc = useQueryClient();
  return useMutation<void, Error, string>({
    mutationFn: (issueKey) =>
      api
        .delete(`${_parentPrefix(parentType, parentId)}/links/${issueKey}`)
        .then(() => undefined),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: jiraKeys.parentIssues(parentType, parentId) });
      qc.invalidateQueries({ queryKey: ["portfolio"] });
    },
  });
}

export function useRefreshJiraIssuesScoped(
  parentType: JiraParentType,
  parentId: string,
) {
  const qc = useQueryClient();
  return useMutation<JiraIssueList, Error, void>({
    mutationFn: () =>
      api
        .post(`${_parentPrefix(parentType, parentId)}/refresh`)
        .then((r) => r.data),
    onSuccess: (data) => {
      qc.setQueryData(jiraKeys.parentIssues(parentType, parentId), data);
      qc.invalidateQueries({ queryKey: ["portfolio"] });
    },
  });
}

// Backward-compat: existing call sites import these names. Thin wrappers
// over the scoped versions so we don't have to touch ProjectDetail or
// the existing Jira UI in this commit.
export function useProjectJiraIssues(projectId: string | null) {
  return useJiraIssues("project", projectId);
}

export function useLinkJiraIssue(projectId: string) {
  return useLinkJiraIssueScoped("project", projectId);
}

export function useUnlinkJiraIssue(projectId: string) {
  return useUnlinkJiraIssueScoped("project", projectId);
}

export function useRefreshJiraIssues(projectId: string) {
  return useRefreshJiraIssuesScoped("project", projectId);
}

export function useJiraTransitions(issueKey: string | null, enabled: boolean) {
  return useQuery<JiraTransition[]>({
    queryKey: jiraKeys.transitions(issueKey ?? ""),
    queryFn: () =>
      api.get(`/jira/issues/${issueKey}/transitions`).then((r) => r.data),
    enabled: Boolean(issueKey) && enabled,
    staleTime: 5 * 60 * 1000,
  });
}

export function useTransitionJiraIssue(projectId: string) {
  const qc = useQueryClient();
  return useMutation<
    JiraIssue,
    Error,
    { issueKey: string; transitionId: string }
  >({
    mutationFn: ({ issueKey, transitionId }) =>
      api
        .post(`/jira/issues/${issueKey}/transition`, {
          transition_id: transitionId,
        })
        .then((r) => r.data),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: jiraKeys.projectIssues(projectId) });
      qc.invalidateQueries({ queryKey: jiraKeys.transitions(vars.issueKey) });
      qc.invalidateQueries({ queryKey: ["portfolio"] });
    },
  });
}
